
import { Component, Prop, VModel, Vue, Watch } from 'vue-property-decorator';
import { compact, map } from 'lodash';
import { isValidNumericInputKey, isValidNotDecimalInputKey } from '@/helpers';
import { ActivityHourMapDTO, ActivityKeyMapDTO, UserEngagementDTO } from '@/models';

interface ManuallyInputProps {
  key: number;
  contribution: number;
  individualDrops: number;
  note: string;
  manuallyInput: ActivityHourMapDTO;
}

@Component
export default class ManuallyInput extends Vue {
  @Prop({ default: false }) disabled: boolean;
  @Prop({ default: false }) isImportedData: boolean;
  @VModel({ default: () => ({ note: '' }) }) manuallyInputModel: ManuallyInputProps;
  @Prop({ default: () => [] }) members: UserEngagementDTO[];
  @Prop({ default: {} }) activityKeyMap: ActivityKeyMapDTO;

  isKeyDisabled = false;

  configVTextField = {
    outlined: true,
    dense: true,
    required: true,
    autocomplete: false
  };

  calculateContributionAndDrops(): void {
    if (this.isImportedData) {
      return;
    }
    if (!this.activityKeyMap || !this.manuallyInputModel?.manuallyInput) {
      this.manuallyInputModel.contribution = 0;
      this.manuallyInputModel.individualDrops = 0;
      return;
    }
    const { contribution, individualDrops } = Object.keys(this.activityKeyMap).reduce(
      (acc, activity) => {
        const contributionHour = Number(this.manuallyInputModel.manuallyInput[activity]) || 0;
        const activityKey = Number(this.activityKeyMap[activity]) || 0;
        return {
          contribution: acc.contribution + contributionHour,
          individualDrops: acc.individualDrops + (contributionHour * activityKey)
        };
      },
      { contribution: 0, individualDrops: 0 }
    );
    this.manuallyInputModel.contribution = contribution;
    this.manuallyInputModel.individualDrops = individualDrops;
  }

  @Watch('manuallyInputModel.manuallyInput', { deep: true })
  onManuallyInputModelChanged(): void {
    this.calculateContributionAndDrops();
  }

  get inputRules(): Record<string, any> {
    return {
      key: [(v: number) => v > 0 || 'Pollens is required'],
      contributionHour: [(v: number) => !!v || 'Contribution is required ', (v: number) => (!!v && v > 0) || 'Min: > 0']
    };
  }

  onKeyDown(event: KeyboardEvent): void {
    const { target } = event as any;
    if (!isValidNumericInputKey(event)) {
      event.preventDefault();
    }
  }

  onKeyInputKeyDown(event: KeyboardEvent): void {
    const { target } = event as any;
    if (!isValidNotDecimalInputKey(event)) {
      event.preventDefault();
    }
    if (target.value && /^0/.test(target.value)) {
      target.value = +target.value.replace(/^0/, '');
    }
  }

  get defaultKey(): number | undefined {
    if (!this.members) {
      return 0;
    }

    const keys = compact(map(this.members || [], 'key'));
    if (keys && keys.length) {
      return +keys[0];
    }
    return Number(this.members[0]?.key);
  }

  handleKeyInputDisabled(): void {
    if (this.defaultKey === 0) {
      this.isKeyDisabled = false;
      return;
    }
    if (this.defaultKey) {
      this.manuallyInputModel.key = this.defaultKey;
      this.isKeyDisabled = true;
      return;
    }
    if (this.members && this.members.length === 0) {
      this.isKeyDisabled = false;
      return;
    }
    this.isKeyDisabled = true;
  }

  created(): void {
    this.calculateContributionAndDrops();
  }

  mounted(): void {
    this.handleKeyInputDisabled();
  }
}
